// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { ActionType, IAction, raiseAction, IActionSortTable } from "actions";
import { IAlarmEntry } from "comtypes";
import { IState } from "states/states";
import { ITableSort } from "states/guistates";

import AlarmEntry from "view/comp/AlarmEntry";
import { makeSortHeader } from "guiutils";
import { isSet } from "utils";
import SearchFilter from "view/comp/SearchFilter";

// uses webpack definitions
declare const IS_CLOUD: boolean;

// sort key of table
const TABLE_NAME = "t_alarm";

interface IAlarmExtProps {
  isDashboard?: boolean;
}

interface IAlarmProps {
  isEditable: boolean;
  alarms: IAlarmEntry[];
  tableSort: ITableSort | undefined;
}

interface IAlarmState {
  filterText: string;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const props: IAlarmProps = {
    isEditable:
      (!IS_CLOUD && state.site.user?.secId === "admin") ||
      (IS_CLOUD && (curUser?.accessRights.ackAlarms ?? false)),
    alarms: state.data.alarms,
    tableSort: state.gui.tableSort[TABLE_NAME] ?? {
      column: "alarmTime",
      isSortAscending: true,
    },
  };
  return props;
};

interface IAlarmDispatch {
  onSort: (sort: IActionSortTable) => void;
}

const messages = defineMessages({
  placeholderSearchField: {
    id: "Search for alarms...",
  },
});

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IAlarmDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
  };
  return dispatchProps;
};

class AlarmComponent extends React.Component<
  IAlarmProps & IAlarmExtProps & IAlarmDispatch & WrappedComponentProps,
  IAlarmState
> {
  state: IAlarmState = {
    filterText: "",
  };

  public render() {
    const sortCol = this.props.tableSort?.column as keyof IAlarmEntry | undefined;
    // data state is immutable so we need to copy it before sorting
    const alarmIds = Array.from(Array(this.props.alarms.length).keys()).sort((a, b) => {
      const keyA = this.props.alarms[a][sortCol!];
      const keyB = this.props.alarms[b][sortCol!];
      const sort = isSet(keyA) && isSet(keyB) ? (keyA > keyB ? 1 : -1) : isSet(keyB) ? 1 : -1;
      return (this.props.tableSort?.isSortAscending !== false ? sort : -sort) as number;
    });
    const alarmRows = alarmIds.map((curItem) => {
      return (
        <AlarmEntry
          key={curItem}
          index={curItem}
          isEditable={this.props.isEditable}
          filterText={this.state.filterText}
        />
      );
    });

    return (
      <div id="alarm">
        {this.props.isDashboard !== true && (
          <div className="tableSearchHeader">
            <h1>
              <FormattedMessage id="Alarms" />
            </h1>
            <SearchFilter
              className="tableSearchBox"
              filterText={this.state.filterText}
              placeholder={this.props.intl.formatMessage(messages.placeholderSearchField)}
              onChange={(filterText) => this.setState({ filterText })}
            />
          </div>
        )}
        {alarmIds.length ? (
          <table className="datatable fullwidthwhite">
            <thead>
              <tr>
                {makeSortHeader(
                  TABLE_NAME,
                  "alarmTime",
                  <FormattedMessage id="Timestamp" />,
                  this.props.tableSort,
                  this.props.onSort,
                  ["center"],
                )}
                {makeSortHeader(
                  TABLE_NAME,
                  "source",
                  <FormattedMessage id="Alarm" />,
                  this.props.tableSort,
                  this.props.onSort,
                )}
                <th className="optional">
                  <FormattedMessage id="Further Information" />
                </th>
                {this.props.isEditable && <th className="alarmAck"></th>}
              </tr>
            </thead>
            <tbody>{alarmRows}</tbody>
          </table>
        ) : (
          <p className="alert alert-success">
            <FormattedMessage id="No alarms pending." />
          </p>
        )}
      </div>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(AlarmComponent));
